import { createAsyncThunk } from '@reduxjs/toolkit';
import { http } from '../../../helpers/request';
import { CreateTagRequest, GetTagsRequest, Tag, TagPaginatedData, TagProductAssociation, TagResponse, UpdateTagRequest } from './types';

const baseUrl = `/tags`;

export const getTagsAction = createAsyncThunk<
    TagResponse<Array<Tag>> | undefined,
    undefined
>('tags/getTags', async () => {
    const result = await http<TagResponse<Array<Tag>>, undefined>({
        path: baseUrl,
        method: 'get',
    });
    return result.parsedBody;
});

export const searchTagsAction = createAsyncThunk<
    TagResponse<TagPaginatedData> | undefined,
    GetTagsRequest & { extraInfo?: boolean }
>('tags/searchTags', async (data) => {
    const { extraInfo, ...body } = data;
    const query = extraInfo ? '?extraInfo=true' : '';
    const result = await http<TagResponse<TagPaginatedData>, GetTagsRequest>({
        path: `${baseUrl}/search${query}`,
        method: 'post',
        body,
    });
    return result.parsedBody;
});

type StateWithTagReducer = {
    tagReducer: {
        searchCache?: Record<string, { fetchedAt: number }>;
        productsByTagId?: Record<number, { fetchedAt?: number }>;
    };
};

const SEARCH_TAGS_TTL_MS = 1000 * 5; // 5 seconds
const TAG_PRODUCTS_TTL_MS = 1000 * 10; // 10 seconds

// Cached search thunk: skips the API call if results for the key are still fresh (TTL)
export const searchTagsCachedAction = createAsyncThunk<
    TagResponse<TagPaginatedData> | undefined,
    GetTagsRequest,
    { state: StateWithTagReducer }
>(
    'tags/searchTagsCached',
    async (data: GetTagsRequest) => {
        const result = await http<TagResponse<TagPaginatedData>, GetTagsRequest>({
            path: `${baseUrl}/search`,
            method: 'post',
            body: data,
        });
        return result.parsedBody;
    },
    {
        condition: (data, { getState }) => {
            const cache = getState().tagReducer.searchCache ?? {};
            const key = (data.searchString || '').toLowerCase();
            const entry = cache[key];
            if (entry && Date.now() - entry.fetchedAt < SEARCH_TAGS_TTL_MS) {
                return false;
            }
            return true;
        },
    },
);

export const createTagAction = createAsyncThunk<
    TagResponse<Tag> | undefined,
    CreateTagRequest
>('tags/createTag', async (data: CreateTagRequest) => {
    const result = await http<TagResponse<Tag>, CreateTagRequest>({
        path: baseUrl,
        method: 'post',
        body: data,
    });
    return result.parsedBody;
});

export const updateTagAction = createAsyncThunk<
    TagResponse<Tag> | undefined,
    { id: number; data: UpdateTagRequest }
>('tags/updateTag', async ({ id, data }) => {
    const result = await http<TagResponse<Tag>, UpdateTagRequest>({
        path: `${baseUrl}/${id}`,
        method: 'put',
        body: data,
    });
    return result.parsedBody;
});

export const deleteTagAction = createAsyncThunk<any, number>(
    'tags/deleteTag',
    async (id: number) => {
        const result = await http<any, number>({
            path: `${baseUrl}/${id}`,
            method: 'delete',
        });
        return result.parsedBody;
    },
);

export const getProductsByTagIdAction = createAsyncThunk<
    { tagId: number; products: TagProductAssociation[] },
    number,
    { state: StateWithTagReducer }
>(
    'tags/getProductsByTagId',
    async (tagId: number) => {
        const result = await http<TagResponse<TagProductAssociation[]>, undefined>({
            path: `${baseUrl}/${tagId}/products`,
            method: 'get',
        });
        const products = result.parsedBody?.data ?? [];
        return { tagId, products };
    },
    {
        condition: (tagId, { getState }) => {
            const entry = getState().tagReducer.productsByTagId?.[tagId];
            if (entry?.fetchedAt && Date.now() - entry.fetchedAt < TAG_PRODUCTS_TTL_MS) {
                return false;
            }
            return true;
        },
    },
);
